/**
 * HOMA PDP interactivity.
 * Reuses homa-slider / homa-tabs when homa-home.js is present;
 * defines them here if the homepage script is not loaded.
 * Also: homa-pdp-tabs (pill tab strip for phone carousel).
 */

function hfArmVideo(video) {
  if (!video) return;
  if (video.dataset.hfVideoArmed !== '1') {
    const source = video.querySelector('source[data-src]');
    const url = video.dataset.src || source?.dataset.src;
    if (!url) return;
    video.dataset.hfVideoArmed = '1';
    if (video.dataset.src) video.src = url;
    else {
      source.src = url;
      video.load();
    }
  }
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (video.hasAttribute('data-autoplay') && !reduced) video.play?.().catch(() => {});
}

(function () {
  if (!customElements.get('homa-slider')) {
    class HomaSlider extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.track = this.querySelector('[data-homa-track]');
        this.slides = [...this.querySelectorAll('[data-homa-slide]')];
        this.dots = [...this.querySelectorAll('[data-homa-dot]')];
        this.prevBtn = this.querySelector('[data-homa-prev]');
        this.nextBtn = this.querySelector('[data-homa-next]');
        this.index = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        this.mode = this.dataset.mode || 'fade';
        this.autoplayMs = Number(this.dataset.autoplay || 0);

        this.prevBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.go(this.index - 1);
          this.restartAutoplay();
        });
        this.nextBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.go(this.index + 1);
          this.restartAutoplay();
        });
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.go(i);
            this.restartAutoplay();
          });
        });
        this.querySelector('[data-homa-buy-zoom]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.classList.toggle('is-expanded');
        });

        if (this.mode === 'scroll' && this.track) {
          this.track.addEventListener(
            'scroll',
            () => {
              window.clearTimeout(this._scrollTimer);
              this._scrollTimer = window.setTimeout(() => this.syncFromScroll(), 60);
            },
            { passive: true }
          );
        }

        this.go(this.index, false);
        this._ready = true;
        this.setupAutoplay();
      }

      disconnectedCallback() {
        window.clearInterval(this._timer);
        this._io?.disconnect();
      }

      setupAutoplay() {
        if (!this.autoplayMs || this.slides.length < 2) return;
        this._io?.disconnect();
        this._io = new IntersectionObserver(
          ([entry]) => {
            this._inView = entry.isIntersecting;
            if (entry.isIntersecting) this.startAutoplay();
            else this.stopAutoplay();
          },
          { threshold: 0.35 }
        );
        this._io.observe(this);
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => {
          if (this._inView !== false) this.startAutoplay();
        });
      }

      startAutoplay() {
        this.stopAutoplay();
        if (!this.autoplayMs || this.slides.length < 2) return;
        this._timer = window.setInterval(() => this.go(this.index + 1), this.autoplayMs);
      }

      stopAutoplay() {
        window.clearInterval(this._timer);
        this._timer = null;
      }

      restartAutoplay() {
        if (this._inView === false) return;
        this.startAutoplay();
      }

      syncFromScroll() {
        if (!this.track || !this.slides.length) return;
        const center = this.track.scrollLeft + this.track.clientWidth / 2;
        let closest = 0;
        let best = Infinity;
        this.slides.forEach((slide, i) => {
          const mid = slide.offsetLeft + slide.offsetWidth / 2;
          const dist = Math.abs(mid - center);
          if (dist < best) {
            best = dist;
            closest = i;
          }
        });
        this.index = closest;
        this.updateChrome();
      }

      go(next, animate = true) {
        if (!this.slides.length) return;
        const len = this.slides.length;
        this.index = ((next % len) + len) % len;

        if (this.mode === 'scroll' && this.track) {
          const slide = this.slides[this.index];
          const left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
          this.track.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
        } else {
          this.slides.forEach((slide, i) => {
            const active = i === this.index;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', active ? 'false' : 'true');
            slide.toggleAttribute('inert', !active);
            slide.removeAttribute('hidden');
            const video = slide.querySelector('video');
            if (video) {
              if (active && this._ready) hfArmVideo(video);
              else if (!active) video.pause?.();
            }
          });
        }
        this.updateChrome();
      }

      updateChrome() {
        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        });
        this.querySelectorAll('[data-homa-tab-btn]').forEach((btn, i) => {
          btn.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
          btn.classList.toggle('is-active', i === this.index);
        });
      }
    }

    customElements.define('homa-slider', HomaSlider);
  }

  // Wire pill tabs that drive a sibling/parent homa-slider
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('[data-homa-tab-btn]');
      if (!btn) return;
      const root = btn.closest('homa-slider') || btn.closest('[data-homa-tabs-root]');
      const slider = root?.tagName === 'HOMA-SLIDER' ? root : root?.querySelector('homa-slider');
      if (!slider || typeof slider.go !== 'function') return;
      const index = Number(btn.dataset.homaTabBtn);
      if (Number.isNaN(index)) return;
      event.preventDefault();
      slider.go(index);
      slider.restartAutoplay?.();
    },
    true
  );

})();

/* Product detail page behaviour.
 *
 * Same shape as homa-home.js: sections register an initialiser that only runs
 * once they scroll near the viewport, and every animation is a CSS transition
 * driven by a class toggle. The design site drives these with GSAP ScrollTrigger
 * and framer-motion; neither is worth 60kB here when the effects are a fade and
 * a translate.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const q = (sel, root = document) => root.querySelector(sel);
const qa = (sel, root = document) => [...root.querySelectorAll(sel)];

const registry = [];

function register(selector, init) {
  registry.push({ selector, init });
}

function observeOnce(el, fn) {
  if (!('IntersectionObserver' in window)) {
    fn();
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      fn();
    },
    { rootMargin: '200px' }
  );
  io.observe(el);
}

function boot() {
  for (const { selector, init } of registry) {
    for (const el of qa(selector)) {
      if (el.dataset.hfReady === '1') continue;
      el.dataset.hfReady = '1';
      observeOnce(el, () => init(el));
    }
  }
}

/* ── Reveal on scroll ───────────────────────────────────────── */

function initReveal() {
  const targets = qa('[data-hf-reveal]');
  if (targets.length === 0) return;

  if (REDUCED.matches || !('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-revealed'));
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  targets.forEach((t) => io.observe(t));
}

/* ── Hero colour carousel + buy form ───────────────────────── */

register('[data-hf-pdp-hero]', (hero) => {
  const slides = qa('[data-hf-pdp-slide]', hero);
  const swatches = qa('[data-hf-pdp-swatch]', hero);
  const colourLabel = q('[data-hf-pdp-colour]', hero);
  const counter = q('[data-hf-pdp-count]', hero);
  const priceEl = q('[data-hf-pdp-price]', hero);
  const variantInput = q('[data-hf-pdp-variant-input]', hero);
  const prev = q('[data-hf-pdp-prev]', hero);
  const next = q('[data-hf-pdp-next]', hero);

  if (slides.length === 0) return;

  let index = 0;

  const hydrateSlide = (slide) => {
    const tpl = slide?.querySelector('[data-hf-pdp-slide-media]');
    if (!tpl) return;
    tpl.replaceWith(tpl.content.cloneNode(true));
  };

  const show = (i) => {
    index = (i + slides.length) % slides.length;
    hydrateSlide(slides[index]);
    hydrateSlide(slides[(index + 1) % slides.length]);

    slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
    swatches.forEach((s, n) => s.classList.toggle('is-active', n === index));

    const active = swatches[index];
    if (colourLabel && active?.dataset.hfColour) {
      colourLabel.textContent = `Color: ${active.dataset.hfColour}`;
    }
    if (counter) counter.textContent = `${index + 1} / ${slides.length}`;

    // A swatch that maps to a real variant drives the buy form, so picking a
    // colour in the hero is the same action as picking it in a variant select.
    if (active?.dataset.hfVariant && variantInput) {
      variantInput.value = active.dataset.hfVariant;
    }
    if (active?.dataset.hfPrice && priceEl) {
      priceEl.innerHTML = active.dataset.hfPrice;
    }
  };

  swatches.forEach((s, n) => s.addEventListener('click', () => show(n)));
  prev?.addEventListener('click', () => show(index - 1));
  next?.addEventListener('click', () => show(index + 1));

  show(0);

  // Lifestyle heroes (LaHear) auto-advance every 5s like the design page.
  if (hero.classList.contains('hf-pdp-hero--lifestyle') && slides.length > 1) {
    let timer = window.setInterval(() => show(index + 1), 5000);
    hero.addEventListener('mouseenter', () => {
      window.clearInterval(timer);
      timer = null;
    });
    hero.addEventListener('mouseleave', () => {
      if (timer) return;
      timer = window.setInterval(() => show(index + 1), 5000);
    });
  }
});

/* ── Auto-advancing feature carousel ───────────────────────── */

register('[data-hf-pdp-carousel]', (root) => {
  const slides = qa('[data-hf-pdp-cslide]', root);
  const dots = qa('[data-hf-pdp-cdot]', root);
  const caption = q('[data-hf-pdp-ccaption]', root);
  const desc = q('[data-hf-pdp-cdesc]', root);
  const tags = q('[data-hf-pdp-ctags]', root);
  if (slides.length < 2) return;

  const interval = Number(root.dataset.hfInterval) || 4000;
  let index = 0;
  let timer = null;

  const show = (i) => {
    index = (i + slides.length) % slides.length;
    slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
    dots.forEach((d, n) => {
      const on = n === index;
      d.classList.toggle('is-active', on);
      // Tabs are the accessible control in tabs mode; dots carry no state.
      if (d.getAttribute('role') === 'tab') d.setAttribute('aria-selected', String(on));
    });
    if (caption) caption.textContent = slides[index].dataset.hfCaption || '';
    if (desc) {
      const text = slides[index].dataset.hfBody || '';
      desc.textContent = text;
      desc.hidden = !text;
    }
    if (tags) {
      const list = (slides[index].dataset.hfTags || '')
        .split('\n')
        .map((t) => t.trim())
        .filter(Boolean);
      tags.replaceChildren(
        ...list.map((t) => {
          const el = document.createElement('span');
          el.className = 'hf-pdp-carousel__tag';
          el.textContent = t;
          return el;
        })
      );
    }
  };

  const start = () => {
    if (REDUCED.matches || root.dataset.hfAutoplay === 'false') return;
    stop();
    timer = setInterval(() => show(index + 1), interval);
  };
  const stop = () => timer && clearInterval(timer);

  dots.forEach((d, n) =>
    d.addEventListener('click', () => {
      show(n);
      start();
    })
  );

  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);

  show(0);
  start();
});

/* ── Detection carousel (manual only) ──────────────────────── */

register('[data-hf-pdp-detect]', (root) => {
  const slides = qa('[data-hf-detect-slide]', root);
  const dots = qa('[data-hf-detect-dot]', root);
  const prev = q('[data-hf-detect-prev]', root);
  const next = q('[data-hf-detect-next]', root);
  if (slides.length < 2) return;

  let index = 0;

  const show = (i) => {
    index = (i + slides.length) % slides.length;

    slides.forEach((s, n) => {
      const on = n === index;
      s.classList.toggle('is-active', on);
      // Inactive slides stay in the DOM for the crossfade, so they have to be
      // hidden from assistive tech explicitly.
      if (on) s.removeAttribute('aria-hidden');
      else s.setAttribute('aria-hidden', 'true');
    });

    dots.forEach((d, n) => {
      const on = n === index;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  };

  dots.forEach((d, n) => d.addEventListener('click', () => show(n)));
  prev?.addEventListener('click', () => show(index - 1));
  next?.addEventListener('click', () => show(index + 1));

  show(0);
});

/* ── Split panel toggle (weather / finish switch) ───────────── */

register('[data-hf-pdp-split]', (root) => {
  const buttons = qa('[data-hf-pdp-split-btn]', root);
  const frames = qa('[data-hf-pdp-split-frame]', root);
  const panel = q('[data-hf-pdp-split-panel]', root);
  if (buttons.length === 0) return;

  const show = (i) => {
    buttons.forEach((b, n) => {
      const on = n === i;
      b.classList.toggle('is-active', on);
      b.style.background = on ? b.dataset.hfActiveBg || 'transparent' : 'transparent';
      // The rainy glyph ships black and needs inverting on the dark chip.
      const img = q('img', b);
      if (img && b.dataset.hfInvertActive === 'true') {
        img.style.filter = on ? 'brightness(0) invert(1)' : '';
      }
    });
    frames.forEach((f, n) => f.classList.toggle('is-active', n === i));
    if (panel && buttons[i]?.dataset.hfPanelBg) {
      panel.style.setProperty('--pdp-media-bg', buttons[i].dataset.hfPanelBg);
    }
  };

  buttons.forEach((b, n) => b.addEventListener('click', () => show(n)));
  show(0);
});

/* ── Tech specs accordion ──────────────────────────────────── */

function openSpecRow(row) {
  row.classList.add('is-open');
  q('.hf-pdp-specs__toggle', row)?.setAttribute('aria-expanded', 'true');
}

register('[data-hf-pdp-specs]', (root) => {
  const rows = qa('[data-hf-pdp-spec-row]', root);

  for (const row of rows) {
    const toggle = q('.hf-pdp-specs__toggle', row);
    toggle?.addEventListener('click', () => {
      const open = row.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }
});

/* The sub-nav's "Tech Specs" link and the /buy-* cross-link both expect the
 * first row open on arrival, matching openTechSpecs() on the design site. */
function initSpecsDeepLink() {
  const specs = q('[data-hf-pdp-specs]');
  if (!specs) return;

  const reveal = () => {
    const first = q('[data-hf-pdp-spec-row]', specs);
    if (first) openSpecRow(first);
  };

  if (window.location.hash === '#tech-specs') {
    // Wait for the accordion's own initialiser so the click handler is bound.
    setTimeout(reveal, 100);
  }

  for (const link of qa('a[href="#tech-specs"]')) {
    link.addEventListener('click', () => setTimeout(reveal, 100));
  }
}

/* ── EQ frames scroll scrub ─────────────────────────────────── */

register('[data-hf-pdp-eq]', (root) => {
  const texts = qa('[data-hf-eq-text]', root);
  const frames = qa('[data-hf-eq-frame]', root);
  const dots = qa('[data-hf-eq-dot]', root);
  const n = frames.length;
  if (n < 2) return;

  let locked = false;
  let idx = 0;

  const set = (i) => {
    idx = Math.max(0, Math.min(n - 1, i));
    texts.forEach((el, j) => el.classList.toggle('is-active', j === idx));
    frames.forEach((el, j) => el.classList.toggle('is-active', j === idx));
    dots.forEach((el, j) => el.classList.toggle('is-active', j === idx));
  };

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      locked = true;
      set(i);
      // Re-enable scroll scrub after the click settles.
      setTimeout(() => {
        locked = false;
      }, 800);
    });
  });

  if (REDUCED.matches) {
    // Stack as a simple stepped reveal via dots only.
    return;
  }

  const onScroll = () => {
    if (locked) return;
    const rect = root.getBoundingClientRect();
    const travel = root.offsetHeight - window.innerHeight;
    if (travel <= 0) return;
    const scrolled = Math.min(Math.max(-rect.top, 0), travel);
    const progress = scrolled / travel;
    set(Math.round(progress * (n - 1)));
  };

  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
});

/* ── Scene carousel (Airport / Restaurant / …) ─────────────── */

register('[data-homa-scene-carousel]', (root) => {
  const copies = qa('[data-homa-scene-copy]', root);
  const images = qa('[data-homa-scene-image]', root);
  const dots = qa('[data-homa-scene-dot]', root);
  const n = copies.length;
  if (n < 2) return;

  const autoplayMs = Number(root.dataset.autoplay || 0);
  let index = Math.max(0, copies.findIndex((el) => el.classList.contains('is-active')));
  let timer = null;
  let inView = true;

  const set = (next) => {
    index = ((next % n) + n) % n;
    copies.forEach((el, i) => el.classList.toggle('is-active', i === index));
    images.forEach((el, i) => el.classList.toggle('is-active', i === index));
    dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  };

  const stop = () => {
    window.clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (!autoplayMs || !inView) return;
    timer = window.setInterval(() => set(index + 1), autoplayMs);
  };

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      set(i);
      start();
    });
  });

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
        else stop();
      },
      { threshold: 0.35 }
    );
    io.observe(root);
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', () => {
      if (inView) start();
    });
  }

  set(index);
  start();
});

/* ── SODA hero carousel ───────────────────────────────────── */

register('[data-hf-soda-hero]', (root) => {
  const slides = qa('[data-hf-soda-slide]', root);
  const prev = q('[data-hf-soda-prev]', root);
  const next = q('[data-hf-soda-next]', root);
  if (slides.length === 0) return;

  let index = 0;
  let timer;

  const show = (i) => {
    index = (i + slides.length) % slides.length;
    slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
  };

  const start = () => {
    clearInterval(timer);
    timer = setInterval(() => show(index + 1), 5000);
  };

  prev?.addEventListener('click', () => {
    show(index - 1);
    start();
  });
  next?.addEventListener('click', () => {
    show(index + 1);
    start();
  });

  start();
});

/* ── SODA features carousel ─────────────────────────────────── */

register('[data-hf-soda-features]', (root) => {
  const slides = qa('[data-hf-soda-feature-slide]', root);
  const dots = qa('[data-hf-soda-feature-dot]', root);
  if (slides.length === 0) return;

  const duration = 8000;
  let index = 0;
  let raf;
  let startTime;

  const toneFor = (i) => slides[i]?.dataset.tone || 'dark';

  const updateBars = (progress) => {
    dots.forEach((dot, n) => {
      const fill = q('[data-hf-soda-feature-fill]', dot);
      if (!fill) return;
      if (n < index) fill.style.width = '100%';
      else if (n === index) fill.style.width = `${progress}%`;
      else fill.style.width = '0%';
    });
  };

  const show = (i) => {
    index = (i + slides.length) % slides.length;
    slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
    dots.forEach((d, n) => {
      d.classList.toggle('is-active', n === index);
      d.setAttribute('aria-current', n === index ? 'true' : 'false');
    });
    root.dataset.tone = toneFor(index);
    slides.forEach((s, n) => {
      const video = s.querySelector('video');
      if (!video) return;
      if (n === index) hfArmVideo(video);
      else video.pause?.();
    });
    startTime = Date.now();
  };

  const tick = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(100, (elapsed / duration) * 100);
    updateBars(progress);
    if (progress >= 100) show(index + 1);
    raf = requestAnimationFrame(tick);
  };

  dots.forEach((dot, n) => {
    dot.addEventListener('click', () => {
      show(n);
    });
  });

  show(0);
  raf = requestAnimationFrame(tick);

  root.addEventListener('mouseenter', () => cancelAnimationFrame(raf));
  root.addEventListener('mouseleave', () => {
    startTime = Date.now();
    raf = requestAnimationFrame(tick);
  });
});

/* ── Boot ──────────────────────────────────────────────────── */

function watchLazyVideos() {
  const videos = qa('video[data-hf-lazy-video]');
  if (!videos.length) return;
  const current = (video) => {
    const slide = video.closest('[data-homa-slide], [data-hf-pdp-slide], [data-hf-soda-feature-slide]');
    return !slide || slide.classList.contains('is-active');
  };
  const sync = (video, on) => {
    if (!on || !current(video)) {
      video.pause?.();
      return;
    }
    hfArmVideo(video);
  };
  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => sync(video, true));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => sync(entry.target, entry.isIntersecting));
    },
    { rootMargin: '400px' }
  );
  videos.forEach((video) => io.observe(video));
}

function init() {
  initReveal();
  initSpecsDeepLink();
  watchLazyVideos();
  boot();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// The theme editor re-renders a single section without a page load.
document.addEventListener('shopify:section:load', init);
